<script setup>
import { computed, watch, onMounted, ref, nextTick } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFandataPerfil } from 'dashboard/composables/useFandataPerfil';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import PainelTorcedor from './fandata/PainelTorcedor.vue';
import NotasTicket from './fandata/NotasTicket.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import TabulacaoDoTicket from './fandata/TabulacaoDoTicket.vue';
import SharedFiles from './SharedFiles.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const {
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

// T-232: o botão ao lado de "mesclar contato" abre a nota da PESSOA. Ela deixou de ocupar o
// corpo do painel, onde disputava espaço com o que o agente escreve todo dia (a nota do ticket),
// e passou a ser um atalho: rara e importante, em vez de frequente e no caminho.
const abrirNotasDoContato = () => {
  // ⚠️ `toggleSidebarUIState` INVERTE o estado, e não o define: a assinatura dela recebe só a
  // chave, e o `true` que estava aqui era descartado em silêncio. Com o bloco já aberto, o botão
  // FECHAVA as notas do contato, e a rolagem suave parava num cabeçalho recolhido, deixando o
  // bloco vizinho no centro da tela. Foi o que a Tamara relatou em 02/10: "quando clico na nota
  // do contato, vai para bloco onde deveria estar a nota da conversa".
  if (!isContactSidebarItemOpen('is_contact_notes_open')) {
    toggleSidebarUIState('is_contact_notes_open');
  }
  nextTick(() => {
    document
      .querySelector('[data-fandata-notas-contato]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
};
const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

// T-147 e T-145: o painel da conversa por perfil, do documento de validação de 31/08.
//
// `contact_attributes` sai para todo mundo: ela apontou que Sócio e CPF já estão no cadastro do
// contato, e repetir na conversa só ocupa a altura que o agente precisa para o que muda.
// `conversation_actions` e `conversation_participants` são gestão e ficam com o administrador.
//
// `conversation_info` saiu da vista do agente em 24/09 (T-231), e NÃO foi apagado: virou bloco de
// administrador. A diferença importa. Protocolo e Sentimento são os únicos lugares onde se corrige
// a IA à mão, e apagar o bloco deixaria o T-204, o cruzamento do Farol, achando classificação
// errada sem ninguém poder consertar. Com ele no administrador, quem calibra a IA é quem cuida da
// operação, e não quem está atendendo.
//
// T-271 (25/09): a coluna da direita passa a ser IGUAL para todo mundo, e a régua deixa de ser
// "agente × administrador". Nas palavras da Tamara na reunião de 24/09: "nos dados do contato, do
// ticket, é a mesma visão do agente, para todo mundo". Quem vê o quê vai ser decidido pelos seis
// perfis (T-263), não por ser ou não administrador.
//
// Então `conversation_actions` e `conversation_participants`, que eram do administrador desde o
// T-147, saem para todos: são gestão, e gestão tem lugar próprio.
//
// `macros` VOLTA para todo mundo. Isto reverte o T-237, e é deliberado: a ordem final do painel
// que a reunião fechou lista "Ações Automatizadas" como um dos quatro blocos que ficam. O T-237
// foi escrito um dia antes, e a reunião é mais recente.
//
// `conversation_info` é a ÚNICA exceção, e é explícita do Douglas em 25/09: fica para o
// Administrador e para o Analista de CS. O Analista ainda não existe como perfil, então hoje é só
// o administrador; quando o T-263 chegar, é só acrescentar. O motivo de não apagar de vez está no
// T-231: Protocolo e Sentimento são os únicos lugares onde se corrige a IA à mão, e sem eles o
// Farol (T-204) acharia classificação errada sem ninguém poder consertar.
//
// `contact_attributes` continua fora de todos desde o T-147, e `shared_files` (Anexos) continua
// para todos por pedido explícito de 24/09.
const { ehAdmin } = useFandataPerfil();

const BLOCOS_FORA_PARA_TODOS = [
  'contact_attributes',
  'conversation_actions',
  'conversation_participants',
];
const BLOCOS_SO_DO_ADMIN = ['conversation_info'];

const filtrarBlocosPorPerfil = itens =>
  itens.filter(
    ({ name }) =>
      !BLOCOS_FORA_PARA_TODOS.includes(name) &&
      (ehAdmin.value || !BLOCOS_SO_DO_ADMIN.includes(name))
  );

onMounted(() => {
  conversationSidebarItems.value = filtrarBlocosPorPerfil(
    conversationSidebarItemsOrder.value
  );
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
});
</script>

<template>
  <div class="w-full">
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
      @close="closeContactPanel"
    />
    <ContactInfo
      :contact="contact"
      :channel-type="channelType"
      @abrir-notas-do-contato="abrirNotasDoContato"
    />
    <div class="px-2 pb-8 list-group">
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div v-if="element.name === 'fandata_painel'">
            <AccordionItem
              :title="
                $t('FANDATA_SIDEBAR.TITLE', {
                  ticket: `TKT-${currentChat.id}`,
                })
              "
              :is-open="isContactSidebarItemOpen('is_fandata_painel_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_fandata_painel_open', value)
              "
            >
              <PainelTorcedor
                :conversation-attributes="conversationAdditionalAttributes"
                :contact="contact"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'fandata_notas'">
            <AccordionItem
              :title="
                $t('FANDATA_NOTAS.TITULO', { ticket: `TKT-${currentChat.id}` })
              "
              :is-open="isContactSidebarItemOpen('is_fandata_notas_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_fandata_notas_open', value)
              "
            >
              <NotasTicket :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'fandata_tabulacao'">
            <AccordionItem
              :title="$t('FANDATA_TABULACAO.TITULO')"
              :is-open="isContactSidebarItemOpen('is_fandata_tabulacao_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_fandata_tabulacao_open', value)
              "
            >
              <TabulacaoDoTicket />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="
                  $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                "
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <AccordionItem
              v-if="contact.id"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <ContactConversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.ACOES_AUTOMATIZADAS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'" data-fandata-notas-contato>
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_contact_notes_open', value)
              "
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'shared_files'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES')"
              :is-open="isContactSidebarItemOpen('is_shared_files_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shared_files_open', value)
              "
            >
              <SharedFiles />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.contact--profile) {
  @apply pb-3 border-b border-solid border-n-weak;
}
</style>
